import { z } from 'zod';
export const AvanceFisicoSchema = z.object({
    worksNo: z.string(),
    taskNo: z.string().nullable(),
    fecha: z.string(),
    versionNo: z.number().int().nullable(),
    description: z.string().nullable(),
    pctAvanceFisico: z.number(),
    outstandingAmount: z.number().nullable(),
});
export const CeldaAvanceMatrizSchema = z.object({
    worksNo: z.string(),
    proyectoDisplay: z.string().nullable(),
    /** Proyecto: prefijo antes del primer '-' (ej. "VN-K.27" → "VN"). */
    bloque: z.string().nullable(),
    /** Bloque: entre primer '-' y primer '.' (ej. "VN-K.27" → "K"). */
    subBloque: z.string().nullable(),
    anio: z.number().int(),
    mes: z.number().int().min(1).max(12),
    /** Semana ISO (1-53, lun-dom) cuando la fila es de granularidad semanal;
     *  ausente/null en las filas mensuales. */
    semanaIso: z.number().int().min(1).max(53).nullable().optional(),
    pctAvance: z.number(),
    // Permiten roll-up correcto a nivel Proyecto/Bloque (vista árbol):
    //   pct_grupo = Σ outstanding / Σ venta sobre las obras del grupo.
    sumOutstanding: z.number(),
    sumPresupuestoVenta: z.number(),
    /** Área prorrateada de la obra (para m² construidos = pctAvance × área). */
    areaM2Total: z.number(),
    /** Valor ganado DEL MES (Σ pct_físico del mes × venta_partida), NO acumulado.
     *  Para m²/monto/% del mes correctos (≠ delta del acumulado clipado). */
    sumValorGanadoMes: z.number(),
    /** % avance del mes = valor ganado del mes / venta total de la casa. */
    pctAvanceMes: z.number(),
    /** Estado de venta de la obra EN ese mes (Formalizado/Entregado/Reservado/
     *  Disponible). Null si la obra no tiene caso ese mes. Para colorear la celda. */
    estadoVenta: z.string().nullable().optional(),
});
export const KpiObraSchema = z.object({
    worksNo: z.string(),
    fechaCalculo: z.string(),
    presupuestoCostoTotal: z.number(),
    presupuestoCostoDirecto: z.number(),
    costoTotal: z.number(),
    costoDirecto: z.number(),
    pctAvancePonderado: z.number(),
    montoAvance: z.number(),
    costoEsperadoSegunAvance: z.number(),
    areaM2Total: z.number(),
    m2Construidos: z.number(),
    cpiDirecto: z.number(),
    pctPresupuestoConsumido: z.number(),
    costoRealXM2: z.number(),
    presupuestoXM2: z.number(),
    pctDesviacionXM2: z.number(),
    alertaSobrecosto: z.string().nullable(),
});
/**
 * Resumen de KPIs por obra para la vista de Costos/m² (tabla tipo BC).
 * Base directa: costo directo, costo avance y desviación excluyen CI.
 */
export const ResumenKpiObraSchema = z.object({
    worksNo: z.string(),
    displayName: z.string().nullable(),
    /** Proyecto: prefijo antes del primer '-' ("VN-J.28" → "VN"). */
    bloque: z.string().nullable(),
    /** Bloque: entre primer '-' y primer '.' ("VN-J.28" → "J"). */
    subBloque: z.string().nullable(),
    pctAvance: z.number(), // fracción 0-1
    areaM2Total: z.number(),
    m2Construidos: z.number(),
    presupuesto: z.number(), // presupuesto costo directo (100% de la obra)
    costoDirecto: z.number(), // costo real directo
    costoIndirecto: z.number(), // costo real CI
    costoAvance: z.number(), // teórico = Σ(%avance_part × presup_costo_part) directo
    desviacion: z.number(), // costoDirecto − costoAvance (+ = sobrecosto)
    costoXm2: z.number(), // costo real directo / m² construidos a la fecha
    cpiDirecto: z.number(),
    alertaSobrecosto: z.string().nullable(),
});
/**
 * KPIs por PARTIDA dentro de una obra (drill-down de Costos/m²).
 */
export const KpiPartidaSchema = z.object({
    worksNo: z.string(),
    taskNo: z.string(),
    tareaDisplay: z.string().nullable(),
    pctAvance: z.number(), // fracción 0-1 (acumulado de la partida)
    costoPresupuesto: z.number(), // presup costo de la partida
    costoAvance: z.number(), // presup × avance
    costoReal: z.number(), // costo real de la partida
    desviacion: z.number(), // real − avance
});
/**
 * Avance acumulado por PARTIDA × mes (drill-down de Avance %).
 */
export const CeldaAvancePartidaSchema = z.object({
    worksNo: z.string(),
    taskNo: z.string(),
    tareaDisplay: z.string().nullable(),
    anio: z.number().int(),
    mes: z.number().int().min(1).max(12),
    pctAvance: z.number(), // acumulado de la partida hasta ese mes
    montoAvance: z.number(), // venta_partida × pctAvance
});
/** Dim.Código consumido en una partida (modal de detalle de partida). */
export const CodigoPartidaSchema = z.object({
    consolidatedCode: z.string().nullable(),
    description: z.string().nullable(),
    tipoCodigo: z.string().nullable(), // 'Producto' / 'Cuenta'
    itemCategoryCode: z.string().nullable(),
    unidadMedida: z.string().nullable(),
    totalQuantity: z.number(),
    totalCostoLcy: z.number(),
});
/** Fila por obra × mes para la pantalla Mensual (Producción / Indirectos). */
export const MensualObraSchema = z.object({
    worksNo: z.string(),
    displayName: z.string().nullable(),
    bloque: z.string().nullable(),
    subBloque: z.string().nullable(),
    anio: z.number().int(),
    mes: z.number().int().min(1).max(12),
    /** Semana ISO (1-53, lun-dom) cuando la fila es de granularidad semanal;
     *  ausente/null en las filas mensuales. */
    semanaIso: z.number().int().min(1).max(53).nullable().optional(),
    /** Producción REAL del mes (movimientos Sale de BC, total_price_lcy). Directa,
     *  no acumulada — el frontend la usa tal cual. */
    produccionMes: z.number(),
    /** Costo indirecto (CI) registrado EN ese mes (gasto del mes, no acumulado). */
    indirectoMes: z.number(),
    /** Costo REAL directo del mes (bi.fact_costos sin SSCC ni partidas CI%). Solo en
     *  filas mensuales; 0/ausente en las semanales. */
    costoMes: z.number().optional(),
    /** Estado de venta de la obra EN ese mes (para colorear la celda). */
    estadoVenta: z.string().nullable().optional(),
});
/**
 * Productividad por obra (vista General + M.O.). El estándar de M.O. (₡/m²) es
 * un parámetro de UI, no del backend — el frontend deriva las columnas teóricas
 * de M.O. multiplicando por el estándar elegido. El backend entrega los crudos.
 */
export const ProductividadObraSchema = z.object({
    worksNo: z.string(),
    displayName: z.string().nullable(),
    /** Proyecto: prefijo antes del primer '-'. */
    bloque: z.string().nullable(),
    /** Bloque: entre primer '-' y primer '.'. */
    subBloque: z.string().nullable(),
    // --- Base ---
    areaM2Total: z.number(), // área prorrateada de la obra
    m2Construidos: z.number(), // avance × área
    pctAvance: z.number(), // fracción 0-1 (avance ponderado)
    // --- General (costo directo) ---
    costoDirecto: z.number(), // costo real directo (Task_No no 'CI%')
    presupuestoDirecto: z.number(), // presupuesto vigente costo directo
    costoAvance: z.number(), // monto avance = Σ(%avance_part × presup_costo_part)
    costoRealXm2: z.number(), // costo directo / m² construidos (0 si m²=0)
    presupuestoXm2: z.number(), // costo teórico x m² = presup directo / área (0 si área=0)
    // --- Mano de Obra (cuenta 6010060000000) ---
    costoMO: z.number(), // Σ total_cost_lcy de consolidated_code='6010060000000'
});
export const RespuestaProductividadSchema = z.array(ProductividadObraSchema);
export const RespuestaAvanceMatrizSchema = z.array(CeldaAvanceMatrizSchema);
export const ListaKpisObraSchema = z.array(KpiObraSchema);
export const RespuestaResumenKpiObraSchema = z.array(ResumenKpiObraSchema);
export const RespuestaKpiPartidaSchema = z.array(KpiPartidaSchema);
export const RespuestaAvancePartidaSchema = z.array(CeldaAvancePartidaSchema);
export const RespuestaCodigosPartidaSchema = z.array(CodigoPartidaSchema);
export const RespuestaMensualObraSchema = z.array(MensualObraSchema);
/**
 * Fila por obra × mes para el reporte "Vivienda Gerencia": producción, indirecto,
 * costo directo y costo de mano de obra de las viviendas (VB/VC/VN, PRO VIVIENDA).
 */
export const ViviendaGerenciaSchema = z.object({
    worksNo: z.string(),
    displayName: z.string().nullable(),
    bloque: z.string().nullable(),
    subBloque: z.string().nullable(),
    anio: z.number().int(),
    mes: z.number().int().min(1).max(12),
    produccion: z.number(),
    indirecto: z.number(),
    costo: z.number(),
    costoMO: z.number(),
});
export const RespuestaViviendaGerenciaSchema = z.array(ViviendaGerenciaSchema);
/**
 * Fila por obra × partida del informe "Vivienda Costos": costo directo y la parte
 * que es mano de obra, para armar el árbol Proyecto > Bloque > Obra > Tarea > Partida.
 */
export const ViviendaCostoSchema = z.object({
    worksNo: z.string(),
    displayName: z.string().nullable(),
    bloque: z.string().nullable(),
    subBloque: z.string().nullable(),
    taskNo: z.string(),
    tareaDisplay: z.string().nullable(),
    tareaPadre: z.string(),
    tareaPadreDisplay: z.string().nullable(),
    costo: z.number(),
    costoMO: z.number(),
});
export const RespuestaViviendaCostoSchema = z.array(ViviendaCostoSchema);
/**
 * Informe "Costo por cuadrilla": mano de obra REAL pagada (BC) repartida entre las
 * subpartidas y cuadrillas de cada obra/partida en proporción a las horas del
 * Control de horas. Respuesta compacta: catálogos aparte para no repetir textos.
 */
export const ViviendaCuadrillasSchema = z.object({
    obras: z.array(z.object({
        worksNo: z.string(),
        displayName: z.string().nullable(),
        /** m² totales de la casa (para el presupuesto teórico de M.O. = estándar × m²). */
        areaM2: z.number().nullable(),
    })),
    partidas: z.array(z.object({ taskNo: z.string(), tareaDisplay: z.string().nullable() })),
    filas: z.array(z.object({
        worksNo: z.string(),
        taskNo: z.string(),
        subactividad: z.string(),
        cuadrilla: z.string(),
        horas: z.number(),
        /** Mano de obra real asignada a esta subpartida/cuadrilla. */
        mo: z.number(),
    })),
});
export const RespuestaViviendaCuadrillasSchema = ViviendaCuadrillasSchema;
/** Obra × mes donde la producción asentada en BC no coincide con las Líneas
 *  producción registradas. */
export const AlertaProduccionSchema = z.object({
    worksNo: z.string(),
    displayName: z.string().nullable(),
    anio: z.number().int(),
    mes: z.number().int().min(1).max(12),
    movimientosBC: z.number(),
    lineasRegistradas: z.number(),
    diferencia: z.number(),
});
export const RespuestaAlertasProduccionSchema = z.array(AlertaProduccionSchema);
//# sourceMappingURL=avance.schema.js.map